/* eslint-disable */
import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { TenantComponents } from 'types/Tenant';

import { QrCodeComponent } from 'pages/qrcode/qrcode.component';
import { ChargingComponent } from 'pages/qrcode/charging/charging.component';
import { RoleGuard } from 'pages/qrcode/guard/qr-code.guard';
import { NotAuthorizedComponent } from 'not-authorized/not-authorized.component';
import { BrowserNotSupportedComponent } from './browser-not-supported/browser-not-supported.component';
import { AdminLayoutComponent } from './layouts/admin/admin-layout.component';
import { AuthLayoutComponent } from './layouts/auth/auth-layout.component';
import { ReleaseNotesComponent } from './release-notes/release-notes.component';
import { Action, Entity } from './types/Authorization';

const routes: Routes = [
  {
    path: 'auth', component: AuthLayoutComponent,
    data: { roles:['A','S','B','D', undefined]},
    loadChildren: () => import('./authentication/authentication.module').then(m => m.AuthenticationModule),
  },
  // Legacy Stripe QR links return to the Tamin/wallet stepper.
  {
    path: 'qrcode/:codedChargingStationID/:codedConnectorID/payment',
    component: QrCodeComponent,
  },
  {
    path: 'qrcode/:codedChargingStationID/:codedConnectorID',
    component: QrCodeComponent,
    loadChildren: () => import('../app/pages/qrcode/qrcode.module').then(m => m.QrCodeModule),
  },
  {
    path: 'qrcode/:codedChargingStationID/:codedConnectorID/charging',
    component: ChargingComponent,
    canActivate: [RoleGuard],
    data: { roles: ['Q','A','S'], setRole: 'Q'  },
    loadChildren: () => import('../app/pages/qrcode/charging/charging.module').then(m => m.ChargingModule),
  },
  {
    path: 'tariffs',
    data: { roles: ['Q','A','S','B','D', undefined] },
    loadChildren: () => import('./shared/pricing-definitions/pricing-definition/tariffs-list/tariffs-list.module').then(m => m.TariffsListModule),
  },
  {
    path: 'verify-email', redirectTo: 'auth/verify-email', pathMatch: 'full',
  },
  {
    path: 'define-password', redirectTo: 'auth/define-password', pathMatch: 'full',
  },
  {
    path: '', component: AdminLayoutComponent,
    canActivate: [RoleGuard],
    data: { roles: ['A','S','B','D', undefined] },
    children: [
      {
        path: '',
        redirectTo: 'statistics',
        pathMatch: 'full'
      },
      {
        path: 'statistics',
        loadChildren: () => import('./pages/statistics/statistics.module').then(m => m.StatisticsModule),
        data: {
          menu: {
            title: 'dashboard',
            type: 'link',
            icon: 'dashboard',
            path: '/statistics',
          },
          auth: {
            entity: Entity.TRANSACTION,
            action: Action.LIST,
          },
          component: TenantComponents.STATISTICS,
        },
      },
      {
        path: 'charging-stations',
        loadChildren: () =>import('./pages/charging-stations/charging-stations.module').then(m => m.ChargingStationsModule),
        data: {
          menu: {
            title: 'charging_stations',
            type: 'link',
            icon: 'ev_station',
            path: '/charging-stations',
          },
          auth: {
            entity: Entity.CHARGING_STATION,
            action: Action.LIST,
          },
        },
      },
      {
        path: 'transactions',
        loadChildren: () => import('./pages/transactions/transactions.module').then(m => m.TransactionsModule),
        data: {
          menu: {
            title: 'transactions',
            type: 'link',
            icon: 'history',
            path: '/transactions',
          },
          auth: {
            entity: Entity.TRANSACTION,
            action: Action.LIST,
          },
        },
      },
      {
        path: 'tenants',
        loadChildren: () => import('./pages/tenants/tenants.module').then(m => m.TenantsModule),
        data: {
          menu: {
            title: 'tenants',
            type: 'link',
            icon: 'home',
            path: '/tenants',
          },
          auth: {
            entity: Entity.TENANT,
            action: Action.LIST,
          },
        },
      },
      {
        path: 'invoices',
        loadChildren: () => import('./pages/invoices/invoices.module').then(m => m.InvoicesModule),
        data: {
          menu: {
            title: 'invoices',
            type: 'link',
            icon: 'insert_drive_file',
            path: '/invoices',
          },
          auth: {
            entity: Entity.INVOICE,
            action: Action.LIST,
          },
          activeInSuperTenant: false,
          displayInSuperTenant: false,
          component: TenantComponents.BILLING,
        },
      },
      {
        path: 'cars',
        loadChildren: () =>  import('./pages/cars/cars.module').then(m => m.CarsModule),
        data: {
          menu: {
            title: 'cars',
            type: 'link',
            icon: 'directions_car',
            path: '/cars',
          },
          auth: [
            { entity: Entity.CAR_CATALOG, action: Action.LIST },
            { entity: Entity.CAR, action: Action.LIST },
          ],
          activeInSuperTenant: true,
          displayInSuperTenant: true,
          component: TenantComponents.CAR,
        },
      },
      {
        path: 'users',
        loadChildren: () => import('./pages/users/users.module').then(m => m.UsersModule),
        data: {
          menu: {
            title: 'users',
            type: 'link',
            icon: 'supervised_user_circle',
            path: '/users',
          },
          auth: {
            entity: Entity.USER,
            action: Action.LIST,
          },
        },
      },
      {
        path: 'charging-station-templates',
        loadChildren: () => import('./pages/charging-station-templates/charging-station-templates.module').then(m => m.ChargingStationTemplatesModule),
        data: {
          menu: {
            title: 'charging_station_templates',
            type: 'link',
            icon: 'assignment',
            path: '/charging-station-templates',
          },
          auth: {
            entity: Entity.CHARGING_STATION_TEMPLATE,
            action: Action.LIST,
          },
          activeInSuperTenant: true,
          displayInSuperTenant: true,
          component: TenantComponents.CHARGING_STATION_TEMPLATE,
        },
      },
      {
        path: 'tags',
        loadChildren: () => import('./pages/tags/tags.module').then(m => m.TagsModule),
        data: {
          menu: {
            title: 'tags',
            type: 'link',
            icon: 'note',
            path: '/tags',
          },
          auth: {
            entity: Entity.TAG,
            action: Action.LIST,
          },
        },
      },
      {
        path: 'assets',
        loadChildren: () => import('./pages/assets/assets.module').then(m => m.AssetsModule),
        data: {
          menu: {
            title: 'assets',
            type: 'link',
            icon: 'bubble_chart',
            path: '/assets',
          },
          auth: {
            entity: Entity.ASSET,
            action: Action.LIST,
          },
          component: TenantComponents.ASSET,
        },
      },
      {
        path: 'organization',
        loadChildren: () => import('./pages/organization/organization.module').then(m => m.OrganizationModule),
        data: {
          menu: {
            title: 'organization',
            type: 'link',
            icon: 'home',
            path: '/organization',
          },
          auth: [
            { entity: Entity.COMPANY, action: Action.LIST },
            { entity: Entity.SITE, action: Action.LIST },
            { entity: Entity.SITE_AREA, action: Action.LIST },
          ],
          component: TenantComponents.ORGANIZATION,
        },
      },
      // {
      //   path: 'statistics',
      //   loadChildren: async () => (await import('./pages/statistics/statistics.module')).StatisticsModule,
      //   data: {
      //     menu: {
      //       title: 'statistics',
      //       type: 'link',
      //       icon: 'assessment',
      //       path: '/statistics',
      //     },
      //     auth: {
      //       entity: Entity.TRANSACTION,
      //       action: Action.LIST,
      //     },
      //     component: TenantComponents.STATISTICS,
      //   },
      // },
      {
        path: 'settings-integration',
        loadChildren: () => import('./pages/settings-integration/settings-integration.module').then(m => m.SettingsIntegrationModule),
        data: {
          menu: {
            title: 'integration_settings',
            type: 'link',
            icon: 'settings',
            path: '/settings-integration',
          },
          auth: {
            entity: Entity.SETTING,
            action: Action.LIST,
          },
        },
      },
      {
        path: 'settings-technical',
        loadChildren: () => import('./pages/settings-technical/settings-technical.module').then(m => m.SettingsTechnicalModule),
        data: {
          menu: {
            title: 'technical_settings',
            type: 'link',
            icon: 'settings_applications',
            path: '/settings-technical',
          },
          auth: {
            entity: Entity.SETTING,
            action: Action.LIST,
          },
        },
      },
      {
        path: 'logs',
        loadChildren: () =>  import('./pages/logs/logs.module').then(m => m.LogsModule),
        data: {
          menu: {
            title: 'logs',
            type: 'link',
            icon: 'reorder',
            path: '/logs',
          },
          auth: {
            entity: Entity.LOGGING,
            action: Action.LIST,
          },
        },
      },
      {
        path: 'release-notes', component: ReleaseNotesComponent,
      },
    ],
  },
  {
    path: 'browser-not-supported', component: BrowserNotSupportedComponent,
    data: { roles: ['A','S','B','D', undefined] },
  },
  {
    path: 'no-authorized', component: NotAuthorizedComponent,
    data: { roles: ['A','S','B','D', undefined] },
  },
  {
    path: '**', redirectTo: 'charging-stations', pathMatch: 'full',
    data: { roles: ['A','S','B','D', undefined] },
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {
    useHash: false,
    scrollPositionRestoration: 'enabled',
  })],
  exports: [RouterModule]
})
export class AppRoutingModule { }
